<template>
  <div v-if="!isConfirmed">
    <div class="form-group">
      <label class="form-label">Group</label>
      <template v-if="targetTutor">
        <ValidationProvider rules="required" ref="fromGroup" v-slot="{ errors }">
          <SelectGroupIdInput :key="targetTutor.id" :tutor="targetTutor" v-model="fromGroupId" />
          <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
        </ValidationProvider>
      </template>
      <template v-else>
        <div class="text-danger">
          Please select Tutor first.
        </div>
      </template>
    </div>

    <div v-if="fromGroup">
      <div class="form-group">
        <label>Students</label>
        <p class="form-confirm-value" v-for="student of fromGroup.students" :key="student.id">
          {{ student.name }}
        </p>
      </div>

      <div class="form-group">
        <label>University</label>
        <div class="form-confirm-value">{{ fromGroup.university.name }}</div>
      </div>

      <div class="form-group">
        <label>Campus</label>
        <div class="form-confirm-value">{{ fromGroup.campus.name }}</div>
      </div>

      <div class="form-group">
        <label>Day of week</label>
        <div class="form-confirm-value">{{ fromGroup.dayOfWeek | weekday }}</div>
      </div>

      <div class="form-group">
        <label>Period</label>
        <div class="form-confirm-value">{{ fromGroup.period }}</div>
      </div>

      <div class="form-group">
        <label>Student</label>
        <ValidationProvider rules="required" ref="student" v-slot="{ errors }">
          <SelectStudentInput v-model="student" :groupId="fromGroup.id" />
          <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
        </ValidationProvider>
      </div>

      <div class="form-group">
        <div>Comment</div>
        <ValidationProvider ref="comment" v-slot="{ errors }">
          <Textarea class="form-control" v-model="comment" />
          <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
        </ValidationProvider>
      </div>
    </div>
  </div>

  <div v-else>
    <div class="form-group">
      <label>Student</label>
      <div class="form-confirm-value">{{ student.name }}</div>
    </div>

    <div v-if="fromGroup" class="form-group mb-5">
      <label>From Group</label>
      <div class="form-confirm-value">
        <div>
          {{ fromGroup.campus.name }}
          {{ fromGroup.fiscalYear }}
          {{ fromGroup.semester.name }}
          {{ fromGroup.dayOfWeek | weekday }}
          Period {{ fromGroup.period }}
        </div>
        <div v-if="fromGroup.booth">
          {{ fromGroup.booth.building }} - {{ fromGroup.booth.room }} - {{ fromGroup.booth.number }}
        </div>
        <div>
          {{ fromGroup.tutor.name }}
        </div>
      </div>
    </div>

    <div class="form-group">
      <label>Comment</label>
      <div class="form-confirm-value">{{ comment }}</div>
    </div>
    <div class="form-group" v-if="isExecuted()">
      <label>{{ status | status }} Comment</label>
      <div class="form-confirm-value">{{ approvedComment }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
} from 'vue-property-decorator'
import _ from 'lodash'
import { ValidationProvider } from 'vee-validate'
import { Tutor } from '@/store/tutors/types'
import { Group, GroupConditions } from '@/store/groups/types'
import { weekdayKey, Weekdays } from '@/store/common.types'
import { IAddApplicationComponent } from '@/components/pages/application/Add.vue'
import SelectGroupIdInput from '@/components/parts/inputItem/SelectGroupIdInput.vue'
import SelectStudentInput from '@/components/parts/inputItem/SelectStudentInput.vue'
import Textarea from '@/components/parts/inputItem/Textarea.vue'
import { SpirApplication } from '@/store/applications/spir/types'
import { Student } from '@/store/students/types'
import { applicationStatusCode, ApplicationStatus } from '@/store/applications/types'

@Component({
  components: {
    ValidationProvider,
    SelectGroupIdInput,
    SelectStudentInput,
    Textarea,
  },
  filters: {
    weekday(code: weekdayKey): string {
      return Weekdays[code]
    },
    status: (s: applicationStatusCode): string => ApplicationStatus[s],
  },
})
export default class ApplicationAddSpir extends Vue implements IAddApplicationComponent {
  @Prop({ required: true }) isConfirmed!: boolean

  // 編集時・詳細時など、初期値が必要な場合
  @Prop() initialValues!: SpirApplication

  @Prop() targetTutor!: Tutor

  @Prop() status!: applicationStatusCode

  private isValidated: boolean = false

  private fromGroupId: string|null = null

  private student: Student|null = null

  private comment: string|null = null

  private approvedComment: string|null = null

  private get fromGroup(): Group|null {
    return this.$store.getters.findGroupById(this.fromGroupId) || null
  }

  @Watch('targetTutor')
  private chengedTutor() {
    const conditions: GroupConditions = {
      tutor: this.targetTutor,
      university: null,
      campuses: [],
      weekdays: [],
      periods: [],
      fiscalYearSemester: null,
    }
    this.$store.dispatch('fetchGroups', { conditions, page: 1 })
  }

  @Watch('fromGroupId')
  private changedGroup(groupId: string) {
    this.$store.dispatch('getGroupById', { groupId })
  }

  private created() {
    if (this.initialValues) {
      this.student = this.initialValues.student
      this.fromGroupId = this.initialValues.fromGroup.id
      this.comment = this.initialValues.comment
      this.approvedComment = this.initialValues?.approvedComment || null
    }

    if (this.targetTutor) {
      this.chengedTutor()
    }
  }

  /**
   * バリデーション（ApplicationAdd.vueから呼び出し）
   * @return {Promise} バリデーション結果
   */
  public validation(): Promise<boolean> {
    return Promise.all([
      'fromGroup', 'student', 'comment',
    ].map((key) => (this.$refs[key] as any).validate()))
      .then((validationResults) => {
        const errorLength = _.filter(validationResults, { valid: false }).length

        if (errorLength === 0) {
          this.isValidated = true
          return true
        }
        return false
      })
  }

  /**
   * 登録（ApplicationAdd.vueから呼び出し）
   * @return {Promise} ActionからのPromise
   */
  public register(tutor: Tutor): Promise<void> {
    const application: SpirApplication = {
      type: 'spir',
      fromGroup: this.fromGroup as Group,
      student: this.student as Student,
      comment: this.comment,
    }

    return this.$store.dispatch('registerSpirApplication', application)
  }

  /**
   * 申請のステータスが未承認以外かチェック
   */
  private isExecuted() {
    return this.status !== 'unapproved'
  }
}
</script>

<style>

</style>
